/* istanbul ignore next */
import { pickBy } from 'lodash-es';
import React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from 'apis/fetch';
import { queryClient } from 'apis/client';
import { defaultFeatureFlags } from 'feature-flags/FeatureFlags';
import { PageHeader } from 'components/PageHeader';
import { InformationCircle, InlineNotification, Input, SearchOutline, ClipboardOutline, ToggleCheckbox } from 'ui';

type AllFlags = Record<string, boolean>;

const cacheTime = 6 * 60 * 1000;

const useAllFeatureFlags = () => {
  const { data } = useQuery({
    cacheTime,
    queryKey: ['allFlags'],
    queryFn: async ({ signal }) => {
      return await api<AllFlags>('/feature-flags/all', { signal });
    },
    placeholderData: defaultFeatureFlags,
  });

  return data ?? defaultFeatureFlags;
};

/* istanbul ignore next */
const FeatureFlagTogglePage: React.FC = () => {
  const [searchInput, setSearchInput] = React.useState('');
  const [copiedEnv, SetCopiedEnv] = React.useState<string>('');

  const allFlags = useAllFeatureFlags();

  const { data: flagsEnv } = useQuery({
    cacheTime,
    queryKey: ['flagsEnv'],
    queryFn: async ({ signal }) => {
      return await api<Record<string, string>>('/feature-flags/env', { signal });
    },
    placeholderData: {} as Record<string, string>,
  });

  const { mutate: toggleFeatureFlag } = useMutation({
    mutationFn: async (param: { key: string; value: boolean }) => {
      const { key, value } = param;
      return api<AllFlags>(`/feature-flags`, {
        method: 'PATCH',
        body: { featureFlags: { [key]: value } },
      });
    },
    onMutate: async (newFlag) => {
      await queryClient.cancelQueries({ queryKey: ['allFlags'] });
      const previousFlags = queryClient.getQueryData(['allFlags']);
      queryClient.setQueryData<AllFlags>(['allFlags'], (old) => ({
        ...old,
        [newFlag.key]: newFlag.value,
      }));

      return { previousFlags };
    },
    onError: (_err, _newFlag, context) => {
      queryClient.setQueryData<AllFlags>(['allFlags'], context?.previousFlags as AllFlags);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries(['allFlags']);
    },
  });

  const onToggleFeatureFlag = async (key: string, value: boolean) => {
    toggleFeatureFlag({ key, value });
  };

  const flags = searchInput
    ? pickBy(allFlags, (flagVal, flagKey) => {
        const si = searchInput.toLowerCase();
        return [
          // by name
          flagKey.toLowerCase().includes(si),
          // by env name
          flagsEnv?.[flagKey]?.toLowerCase().includes(si),
          // by value
          si === 'true' && flagVal,
          si === 'false' && !flagVal,
        ].some(Boolean);
      })
    : allFlags;

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="flex flex-col col-span-full gap-y-6">
        <PageHeader headerText="Feature Flags" />
        <InlineNotification
          variant="neutral"
          icon={<InformationCircle />}
          message={`Changes made on this page will be reset after the server restarts. To make changes persist, update the .env file`}
        />
        <Input
          unpaddedPrefix={<SearchOutline className="flex-shrink-0 text-gray-60 w-5 h-5 ml-4" />}
          value={searchInput}
          onInputChange={setSearchInput}
          placeholder={`Search feature flags by name/value(true,false)`}
        />
        <div className="bg-white rounded-lg p-4 min-w-[960px]">
          {Object.entries(flags)
            .sort(([kA], [kB]) => kA.localeCompare(kB))
            .map(([k, v]) => {
              const envName = flagsEnv?.[k];
              return (
                <div key={k} className={`items-center p-2 border-b last:border-b-0 grid grid-cols-12`}>
                  <div className="col-span-6">{k}</div>
                  <div className="captions col-span-5 flex flex-row items-center">
                    <code>{envName}</code>
                    {envName && (
                      <ClipboardOutline
                        className="w-4 h-4 ml-1 cursor-pointer"
                        onClick={() => {
                          navigator.clipboard.writeText(`${envName}=${v}`);
                          SetCopiedEnv(envName);
                          setTimeout(() => SetCopiedEnv(''), 1000);
                        }}
                      />
                    )}
                    {envName === copiedEnv && <div>copied!</div>}
                  </div>
                  <div className="flex justify-end">
                    <ToggleCheckbox isSelected={!!v} setSelected={() => onToggleFeatureFlag(k, !v)} />
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default FeatureFlagTogglePage;
